import { XCircle, ArrowRight, RefreshCw, Home, AlertTriangle, CreditCard } from "lucide-react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

const PaymentFailed = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-red-50 to-orange-50 flex items-center justify-center p-4">
        <div className="w-full max-w-2xl">
          <Card className="border-0 shadow-2xl bg-white/80 backdrop-blur-sm">
            <CardHeader className="text-center pb-8">
              <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-red-500 to-orange-500 rounded-full mb-6 shadow-xl">
                <XCircle className="w-12 h-12 text-white" />
              </div>
              <CardTitle className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                Payment Failed!
              </CardTitle>
              <CardDescription className="text-lg text-gray-600">
                We couldn't process your payment. Don't worry, no charges have been made.
              </CardDescription>
            </CardHeader>
            
            <CardContent className="space-y-8">
              {/* Error Details */}
              <div className="bg-red-50 rounded-2xl p-6 border border-red-200">
                <div className="flex items-center justify-center space-x-4 mb-4">
                  <div className="w-12 h-12 bg-red-500 rounded-xl flex items-center justify-center">
                    <AlertTriangle className="w-6 h-6 text-white" />
                  </div>
                  <div className="text-center">
                    <h3 className="text-xl font-semibold text-red-800">Transaction Failed</h3>
                    <p className="text-red-600">Error Code: #PAY_001</p>
                  </div>
                </div>
                
                <div className="space-y-3 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Attempted Amount:</span>
                    <span className="font-semibold text-gray-900">₹299</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Payment Method:</span>
                    <span className="font-semibold text-gray-900">UPI</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Time:</span>
                    <span className="font-semibold text-gray-900">2:30 PM</span>
                  </div>
                </div>
              </div>

              {/* Possible Reasons */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Possible Reasons</h3>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                    <CreditCard className="w-5 h-5 text-yellow-600" />
                    <span className="text-sm text-yellow-800">Insufficient funds in your account</span>
                  </div>
                  <div className="flex items-center space-x-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <AlertTriangle className="w-5 h-5 text-blue-600" />
                    <span className="text-sm text-blue-800">Network connectivity issues</span>
                  </div>
                  <div className="flex items-center space-x-3 p-3 bg-purple-50 rounded-lg border border-purple-200">
                    <XCircle className="w-5 h-5 text-purple-600" />
                    <span className="text-sm text-purple-800">Payment gateway timeout</span>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/booking" className="flex-1">
                  <Button className="w-full bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white shadow-lg hover:shadow-xl transition-all duration-300 group">
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Try Again
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                <Link to="/" className="flex-1">
                  <Button variant="outline" className="w-full border-gray-200 hover:bg-gray-50">
                    <Home className="w-4 h-4 mr-2" />
                    Back to Home
                  </Button>
                </Link>
              </div>

              {/* Support Information */}
              <div className="bg-gray-50 rounded-2xl p-6 border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Need Help?</h3>
                <p className="text-sm text-gray-600 mb-4">
                  If you continue to experience issues, please contact our support team.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button variant="outline" className="flex-1 border-gray-200 hover:bg-gray-50">
                    <CreditCard className="w-4 h-4 mr-2" />
                    Contact Support
                  </Button>
                  <Button variant="outline" className="flex-1 border-gray-200 hover:bg-gray-50">
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Check Payment Methods
                  </Button>
                </div>
              </div>

              {/* Trust Indicators */}
              <div className="flex items-center justify-center space-x-6 text-sm text-gray-500 pt-4 border-t border-gray-200">
                <div className="flex items-center space-x-2">
                  <XCircle className="w-4 h-4 text-green-500" />
                  <span>No Charges Made</span>
                </div>
                <div className="flex items-center space-x-2">
                  <XCircle className="w-4 h-4 text-blue-500" />
                  <span>Secure Process</span>
                </div>
                <div className="flex items-center space-x-2">
                  <XCircle className="w-4 h-4 text-purple-500" />
                  <span>24/7 Support</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
    </div>
  );
};

export default PaymentFailed;
